import { Check, Search, X } from '@/components/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { LIMITS, type PresenceStatus, type PublicUser } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { conversationApi } from '@/api/endpoints';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { conversationTitle } from '@/lib/conversations';
import { cn } from '@/lib/utils';
import { useConversations, useFriends } from '@/stores/social';
import { toast } from '@/stores/ui';
import { usePresence, useUsers } from '@/stores/users';

/**
 * Pick friends to message. One friend opens your DM with them; more make a
 * group. From a DM it starts a group with that person in it, and from a
 * group it adds people to it.
 */
export function NewDmModal({ conversationId, onClose }: { conversationId?: string; onClose: () => void }) {
  const status = useFriends((s) => s.status);
  const friends = useFriends((s) => s.friends);
  const users = useUsers((s) => s.users);
  const presences = usePresence((s) => s.statuses);
  const from = useConversations((s) => (conversationId ? s.conversations[conversationId] : undefined));
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    void useFriends.getState().load();
  }, []);

  const addingToGroup = from?.type === 'GROUP';
  // People already in the conversation stay in it: they're listed, but can't be picked.
  const already = useMemo(() => new Set(from?.recipients.map((u) => u.id) ?? []), [from]);
  // Everyone counts toward the group limit, you included.
  const room = LIMITS.groupDmMaxMembers - 1 - already.size;

  const list = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return Object.values(friends)
      .map((f) => users[f.user.id] ?? f.user)
      .filter((u) => !q || u.displayName.toLowerCase().includes(q) || u.username.includes(q))
      .sort((a, b) => Number(already.has(a.id)) - Number(already.has(b.id)) || a.displayName.localeCompare(b.displayName));
  }, [friends, users, filter, already]);

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < room ? [...p, id] : p));
  const makesGroup = addingToGroup || !!from || picked.length > 1;

  const submit = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    try {
      let conversation;
      if (addingToGroup) conversation = await conversationApi.addMembers(from.id, picked);
      else if (from) conversation = await conversationApi.createGroup([...already, ...picked], name.trim() || null);
      else if (picked.length === 1) conversation = await conversationApi.open(picked[0]!);
      else conversation = await conversationApi.createGroup(picked, name.trim() || null);
      useConversations.getState().upsert(conversation);
      onClose();
      navigate(`/app/@me/${conversation.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(false);
    }
  };

  const title = addingToGroup ? `Add friends to ${conversationTitle(from)}` : from ? 'Start a group' : 'Start a conversation';
  const description = addingToGroup
    ? `Groups can have up to ${LIMITS.groupDmMaxMembers} people.`
    : from
      ? `Pick friends to start a group with ${conversationTitle(from)}. Your messages with them stay where they are.`
      : `Pick one friend for a direct message, or up to ${LIMITS.groupDmMaxMembers - 1} for a group.`;
  const action = addingToGroup ? 'Add to group' : makesGroup ? 'Create group' : 'Message';

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <span className="mr-auto text-xs text-fg-muted" aria-live="polite">
            {picked.length > 0 && `${picked.length} selected · room for ${room - picked.length} more`}
          </span>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={picked.length === 0} loading={busy}>
            {action}
          </Button>
        </>
      }
    >
      {picked.length > 0 && (
        <ul className="mb-3 flex flex-wrap gap-1.5" aria-label="Selected friends">
          {picked.map((id) => {
            const u = users[id] ?? friends[id]?.user;
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  aria-label={`Remove ${u?.displayName ?? 'friend'}`}
                  className="flex h-7 items-center gap-1.5 rounded-full bg-accent-soft pr-2 pl-1 text-[13px] font-semibold text-fg hover:bg-active"
                >
                  <Avatar user={u ?? null} size={20} decorate={false} />
                  {u?.displayName}
                  <X size={12} className="text-fg-muted" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <label className="relative mb-3 block">
        <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-muted" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search friends"
          aria-label="Search friends"
          data-autofocus
          className="h-10 w-full rounded-xl border border-line bg-sunken pr-3 pl-9 text-sm focus:border-accent focus:outline-none"
        />
      </label>
      {status !== 'loaded' ? (
        <div className="flex justify-center py-8 text-fg-muted">
          <Spinner />
        </div>
      ) : list.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-muted">{Object.keys(friends).length === 0 ? "You haven't added any friends yet." : 'No friends match.'}</p>
      ) : (
        <ul className="-mx-2 max-h-72 overflow-y-auto" aria-label="Friends">
          {list.map((u) => (
            <FriendOption
              key={u.id}
              user={u}
              presence={presences[u.id] ?? 'offline'}
              state={already.has(u.id) ? 'member' : picked.includes(u.id) ? 'picked' : picked.length >= room ? 'full' : 'open'}
              onToggle={() => toggle(u.id)}
            />
          ))}
        </ul>
      )}
      {makesGroup && !addingToGroup && picked.length > 0 && (
        <div className="mt-3">
          <Input label="Group name (optional)" value={name} onChange={(e) => setName(e.target.value)} maxLength={LIMITS.groupNameMax} placeholder="Named after its members" />
        </div>
      )}
    </Modal>
  );
}

function FriendOption({
  user,
  presence,
  state,
  onToggle,
}: {
  user: PublicUser;
  presence: PresenceStatus;
  state: 'open' | 'picked' | 'member' | 'full';
  onToggle: () => void;
}) {
  const disabled = state === 'member' || state === 'full';
  return (
    <li>
      <button
        type="button"
        role="checkbox"
        aria-checked={state === 'picked' || state === 'member'}
        disabled={disabled}
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-hover disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
      >
        <Avatar user={user} size={34} status={presence} ring="ring-elevated" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{user.displayName}</span>
          <span className="block truncate text-xs text-fg-muted">{state === 'member' ? 'Already in the group' : `@${user.username}`}</span>
        </span>
        <span
          aria-hidden
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
            state === 'picked' || state === 'member' ? 'border-accent bg-accent text-accent-fg' : 'border-line-strong',
          )}
        >
          {(state === 'picked' || state === 'member') && <Check size={13} weight="bold" />}
        </span>
      </button>
    </li>
  );
}
